import { writeCheck, writeError } from "./_helpers/context.mjs";
import { lazyProperties } from "./_helpers/lazy-properties.mjs";
import { CheckKind } from "./checks/check-kind.mjs";
import { NOOP_CONTEXT } from "./index.mjs";
export const name = "union";
export class TryUnionType {
  constructor(options) {
    this.name = name;
    this._options = options;
    if (typeof options !== "function") {
      this._applyOptions();
    } else {
      lazyProperties(this, this._applyOptions, ["variants"]);
    }
  }
  match(cx, value) {
    const variantChecks = [];
    for (const variant of this.variants) {
      const {
        ok,
        value: actual
      } = variant.test(cx, value);
      if (ok) {
        return {
          ok: true,
          value: {
            type: variant,
            value: actual
          }
        };
      } else {
        variantChecks.push(actual);
      }
    }
    const source = writeCheck(cx, {
      check: CheckKind.Aggregate,
      children: variantChecks
    });
    return writeError(cx, {
      check: CheckKind.UnionMatch,
      children: [source]
    });
  }
  matchTrusted(value) {
    const {
      ok,
      value: actual
    } = this.match(NOOP_CONTEXT, value);
    if (ok) {
      return actual;
    }
    throw new Error("no matching variant found for `TryUnion` value");
  }
  write(writer, value) {
    const variant = this.matchTrusted(value).type;
    if (variant.write === undefined) {
      throw new Error(`write is not supported for TryUnion with non-writable variant ${variant.name}`);
    }
    return variant.write(writer, value);
  }
  read(cx, reader, raw) {
    const {
      ok,
      value
    } = this.variantRead(cx, reader, raw);
    if (ok) {
      return {
        ok: true,
        value: value.value
      };
    } else {
      return {
        ok: false,
        value
      };
    }
  }
  variantRead(cx, reader, raw) {
    const variantChecks = [];
    for (const variant of this.variants) {
      if (variant.read === undefined) {
        throw new Error(`read is not supported for TryUnion with non-readable variant ${variant.name}`);
      }
      const {
        ok,
        value: actual
      } = variant.read(cx, reader, raw);
      if (ok) {
        return {
          ok: true,
          value: {
            type: variant,
            value: actual
          }
        };
      } else {
        variantChecks.push(actual);
      }
    }
    const source = cx.write({
      check: CheckKind.Aggregate,
      children: variantChecks
    });
    return writeError(cx, {
      check: CheckKind.UnionMatch,
      children: [source]
    });
  }
  test(cx, value) {
    const {
      ok,
      value: actual
    } = this.match(cx, value);
    if (ok) {
      return {
        ok: true,
        value: actual.value
      };
    } else {
      return {
        ok: false,
        value: actual
      };
    }
  }
  equals(val1, val2) {
    const match1 = this.matchTrusted(val1);
    const match2 = this.matchTrusted(val2);
    return match1.type === match2.type && match1.type.equals(match1.value, match2.value);
  }
  clone(val) {
    return this.matchTrusted(val).type.clone(val);
  }
  _applyOptions() {
    if (this._options === undefined) {
      throw new Error("missing `_options` for lazy initialization");
    }
    const options = typeof this._options === "function" ? this._options() : this._options;
    delete this._options;
    const variants = options.variants;
    Object.assign(this, {
      variants
    });
  }
}
